import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./Chatbot.css";
import sendMessage from "./ChatbotAi";
import ReactMarkdown from "react-markdown";
import { useStatistics } from "./hooks/useStats";

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hai! 👋 Saya LaKas, ada yang bisa saya bantu?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const { stats } = useStatistics();

  // 🟢 Minimize (not close) when changing page
  useEffect(() => {
    setIsExpanded(false);
  }, [location]);

  // 🟢 Toggle chat open/close
  const toggleChat = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (!prev) {
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: "auto" });
        }, 50);
      }
      return newState;
    });
  };

  // 🟢 Toggle expand/minimize
  const toggleExpand = () => setIsExpanded((prev) => !prev);

  // 🟢 Send message handler
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { from: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const summaryUser = stats ? JSON.stringify(stats) : "";
      const botReply = await sendMessage(input, summaryUser);
      setMessages((prev) => [...prev, { from: "bot", text: botReply }]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: "Maaf 😔, terjadi kesalahan saat menghubungi server AI.",
        },
      ]);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.style.height = "40px";
    }
  };

  // 🟢 Auto-scroll on new messages
  useEffect(() => {
    if (isOpen) chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  // 🟢 Auto-resize textarea height
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(
        inputRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [input]);

  return (
    <>
      {/* 🟢 Floating Icon */}
      <div
        className={`chatbot-icon ${isOpen ? "hidden-tooltip" : ""}`}
        onClick={toggleChat}
      >
        <img src="https://github.com/user-attachments/assets/b151904c-dd3e-4b1c-81e4-9d0e9465c855" alt="Chat Icon" />
      </div>

      {/* 🟢 Chatbox */}
      {isOpen && (
        <div className={`chatbot-box ${isExpanded ? "expanded" : ""}`}>
          <div className="chat-header">
            <h4>LaKas Chatbot</h4>
            <div className="header-buttons">
              <button className="resize-btn" onClick={toggleExpand}>
                {isExpanded ? "🗕" : "🗖"}
              </button>
              <button className="close-btn" onClick={toggleChat}>
                ×
              </button>
            </div>
          </div>

          <div className="chat-body">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={msg.from === "user" ? "user-msg" : "bot-msg"}
              >
                <ReactMarkdown>{msg.text}</ReactMarkdown>
              </div>
            ))}
            {loading && <p className="bot-msg">Mengetik... </p>}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input">
            <textarea
              ref={inputRef}
              placeholder="Ketik pesan..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows="1"
              style={{
                resize: "none",
                overflowY: "auto",
                maxHeight: "120px",
              }}
            />
            <button onClick={handleSend}>Kirim</button>
          </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;
